import {
  Dialog, Box, Typography, Grid, Paper,
  Chip, Divider, Button, CircularProgress,
  Alert, IconButton,
} from "@mui/material";
import CloseIcon             from "@mui/icons-material/Close";
import CurrencyRupeeIcon     from "@mui/icons-material/CurrencyRupee";
import PersonIcon            from "@mui/icons-material/Person";
import DirectionsBikeIcon    from "@mui/icons-material/DirectionsBike";
import SwapHorizIcon         from "@mui/icons-material/SwapHoriz";
import AccountBalanceIcon    from "@mui/icons-material/AccountBalance";
import ReceiptIcon           from "@mui/icons-material/Receipt";
import PendingActionsIcon    from "@mui/icons-material/PendingActions";
import CardGiftcardIcon      from "@mui/icons-material/CardGiftcard";
import TwoWheelerIcon        from "@mui/icons-material/TwoWheeler";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import EvStationIcon         from "@mui/icons-material/EvStation";

/* ─── helpers ─────────────────────────────────────────────────── */
const fmtMoney = (n) =>
  n !== undefined && n !== null && n !== "" && !isNaN(n)
    ? "₹" + Number(n).toLocaleString("en-IN")
    : "—";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/* ─── payment slip printer ────────────────────────────────────── */
const openPaymentSlip = (d) => {
  const fmt = (n) => n != null && !isNaN(n)
    ? "₹" + Number(n).toLocaleString("en-IN") : "—";
  const fdate = (dt) => dt
    ? new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const remaining   = d.due?.remainingAmount ?? 0;
  const statusColor = remaining > 0 ? "#c45100" : "#2e7d32";
  const statusBg    = remaining > 0 ? "#fff3e0" : "#e8f5e9";

  const payRows = (d.due?.payments || []).map(p => `
    <tr>
      <td>${fdate(p.date)}</td>
      <td>${p.note || "—"}</td>
      <td style="text-align:right;font-weight:600">${fmt(p.amount)}</td>
    </tr>`).join("");

  const slipHTML = `
  <div class="slip">
    <div class="slip-header">
      <div class="doc-label">PAYMENT DUE SLIP</div>
      <div class="company">BANKALI ENTERPRISE</div>
      <div class="addr">Vill+P.O. Chaitanyapur (Barada Rail Gate), Sutahata, Dist. East Medinipur, WB 721658</div>
      <div class="addr">Ph: 7047233089 / 4779148045 &nbsp;|&nbsp; GSTIN: 19DKBPG0462C1ZN</div>
    </div>
    <div class="inv-bar">
      <div>
        <div class="lbl">Invoice No.</div>
        <div class="val blue">${d.invoiceNo || "—"}</div>
      </div>
      <div style="text-align:center">
        <div class="lbl">Sale Date</div>
        <div class="val">${fdate(d.saleDate)}</div>
      </div>
      <div style="text-align:right">
        <div class="lbl">Status</div>
        <span class="badge" style="background:${statusBg};color:${statusColor}">${d.due?.status || "—"}</span>
      </div>
    </div>
    <div class="two-col">
      <div class="col-block">
        <div class="sec-title">Customer Details</div>
        <div class="row2"><span>Name</span><span>${d.customerName || "—"}</span></div>
        <div class="row2"><span>Father</span><span>${d.fatherName || "—"}</span></div>
        <div class="row2"><span>Mobile</span><span>${Array.isArray(d.mobile) ? d.mobile.join(", ") : (d.mobile || "—")}</span></div>
        <div class="row2"><span>Address</span><span>${[d.village, d.postOffice, d.pinCode].filter(Boolean).join(", ") || d.address || "—"}</span></div>
      </div>
      <div class="col-block">
        <div class="sec-title">Vehicle Details</div>
        <div class="row2"><span>Model</span><span>${d.model || "—"}</span></div>
        <div class="row2"><span>Type</span><span>${(d.vehicleType || "").replace("_", " ")}</span></div>
        <div class="row2"><span>Engine No.</span><span style="font-family:monospace">${d.engineNumber || "—"}</span></div>
        <div class="row2"><span>Serial No.</span><span style="font-family:monospace">${d.chasisNumber || "—"}</span></div>
      </div>
    </div>
    <div class="section">
      <div class="sec-title">Description</div>
      <table class="desc-table">
        <tr class="total-row"><td>Total Paid Amount</td><td style="text-align:right">${fmt(d.cashAmount)}</td></tr>
        <tr class="total-row"><td>Total Due Amount</td><td style="text-align:right">${fmt(d.due?.totalAmount)}</td></tr>
        <tr><td>Paid Amount</td><td style="text-align:right;color:#2e7d32;font-weight:600">${fmt(d.due?.paidAmount)}</td></tr>
        <tr class="rem-row"><td><strong>Remaining Amount</strong></td><td style="text-align:right;color:${statusColor};font-weight:700">${fmt(d.due?.remainingAmount)}</td></tr>
      </table>
      ${d.remarks ? `<div class="row2" style="margin-top:4px"><span>Remarks</span><span>${d.remarks}</span></div>` : ""}
    </div>
    ${(d.due?.payments?.length > 0) ? `
    <div class="section">
      <div class="sec-title">Payment History (${d.due.payments.length})</div>
      <table class="pay-table">
        <thead><tr><th>Date</th><th>Note</th><th style="text-align:right">Amount</th></tr></thead>
        <tbody>${payRows}</tbody>
      </table>
    </div>` : ""}
    <div class="sign-row">
      <div class="sign-box">
        <div class="sign-line"></div>
        <div class="sign-label">Customer Signature</div>
      </div>
      <div class="sign-box" style="text-align:right">
        <div class="sign-line"></div>
        <div class="sign-label">Authorised Signatory</div>
        <div class="sign-label" style="font-weight:600">Bankali Enterprise</div>
      </div>
    </div>
  </div>`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Payment Slip — ${d.invoiceNo}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:11px;background:#e8e8e8}
.toolbar{position:sticky;top:0;z-index:99;background:#1565C0;padding:8px 16px;display:flex;gap:10px;align-items:center;justify-content:flex-end}
.toolbar span{color:#fff;font-size:12px;font-weight:600;flex:1}
.btn{padding:7px 22px;border:none;border-radius:5px;font-size:12px;font-weight:700;cursor:pointer}
.btn-print{background:#fff;color:#1565C0}
.btn-close{background:transparent;color:#fff;border:1px solid rgba(255,255,255,.5)}
.btn-print:hover{background:#e3f2fd}
.btn-close:hover{background:rgba(255,255,255,.15)}
.page{width:210mm;min-height:297mm;margin:12px auto;background:#fff;padding:0;display:flex;flex-direction:column}
.copy-wrap{flex:1;padding:8mm 10mm;border-bottom:2px dashed #bbb;position:relative}
.copy-wrap:last-child{border-bottom:none}
.copy-tag{position:absolute;top:6px;right:10px;font-size:8px;font-weight:700;color:#aaa;letter-spacing:1px;text-transform:uppercase}
.slip-header{text-align:center;padding-bottom:5px;border-bottom:2px solid #1565C0;margin-bottom:5px}
.doc-label{font-size:8px;font-weight:700;letter-spacing:2px;color:#888;text-transform:uppercase;margin-bottom:2px}
.company{font-size:16px;font-weight:700;color:#1565C0;letter-spacing:.5px}
.addr{font-size:8px;color:#555;line-height:1.4}
.inv-bar{display:flex;justify-content:space-between;align-items:flex-start;background:#f4f6fa;padding:5px 8px;border-radius:4px;margin-bottom:5px;font-size:10px}
.lbl{font-size:8px;color:#999;font-weight:700;text-transform:uppercase;letter-spacing:.4px}
.val{font-size:11px;font-weight:700;color:#1a1a1a;margin-top:1px}
.blue{color:#1565C0}
.badge{font-size:9px;font-weight:700;padding:2px 8px;border-radius:10px;display:inline-block;margin-top:2px}
.two-col{display:flex;gap:6px;margin-bottom:5px}
.col-block{flex:1;border:0.5px solid #e0e0e0;border-radius:4px;padding:5px 7px}
.sec-title{font-size:8px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:.8px;margin-bottom:4px;padding-bottom:3px;border-bottom:.5px solid #eee}
.row2{display:flex;justify-content:space-between;font-size:9.5px;padding:2px 0;border-bottom:.5px solid #f5f5f5;gap:4px}
.row2:last-child{border:none}
.row2 span:first-child{color:#777;white-space:nowrap}
.row2 span:last-child{font-weight:600;text-align:right}
.section{border:.5px solid #e0e0e0;border-radius:4px;padding:5px 7px;margin-bottom:5px}
.desc-table{width:100%;border-collapse:collapse;font-size:10px}
.desc-table td{padding:3px 4px;border-bottom:.5px solid #f0f0f0}
.desc-table tr:last-child td{border:none}
.total-row td{font-weight:600}
.rem-row td{background:#fff9f5;border-radius:2px;font-size:11px}
.pay-table{width:100%;border-collapse:collapse;font-size:9px}
.pay-table thead tr{background:#f4f6fa}
.pay-table th{padding:3px 5px;font-size:8px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid #e0e0e0;text-align:left}
.pay-table td{padding:3px 5px;border-bottom:.5px solid #f0f0f0;color:#444}
.pay-table tr:last-child td{border:none}
.sign-row{display:flex;justify-content:space-between;margin-top:8px;padding-top:4px}
.sign-box{width:42%}
.sign-line{border-bottom:1px solid #333;margin-bottom:3px;height:22px}
.sign-label{font-size:8.5px;color:#555}
@media print{
  body{background:#fff}
  .toolbar{display:none}
  .page{margin:0;width:100%;min-height:0;box-shadow:none}
  .copy-wrap{padding:6mm 8mm}
}
</style></head>
<body>
<div class="toolbar">
  <span>Payment Due Slip — ${d.invoiceNo || ""}</span>
  <button class="btn btn-close" onclick="window.close()">✕ Close</button>
  <button class="btn btn-print" onclick="window.print()">🖨 Print</button>
</div>
<div class="page">
  <div class="copy-wrap">
    <div class="copy-tag">Customer Copy</div>
    ${slipHTML}
  </div>
  <div class="copy-wrap">
    <div class="copy-tag">Office Copy</div>
    ${slipHTML}
  </div>
</div>
</body></html>`;

  const w = window.open("", "_blank", "width=820,height=900,scrollbars=yes");
  w.document.write(html);
  w.document.close();
};

/* ─── label-value row ─────────────────────────────────────────── */
const DR = ({ label, value, mono = false }) => (
  <Box sx={{
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    py: 0.75, borderBottom: "0.5px solid", borderColor: "divider",
    "&:last-child": { borderBottom: "none" },
  }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}
      sx={{ fontSize: 11, minWidth: 130, letterSpacing: 0.2 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} textAlign="right"
      sx={{ fontFamily: mono ? "monospace" : "inherit", fontSize: 13, wordBreak: "break-all" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

/* ─── section card ────────────────────────────────────────────── */
const VCard = ({ title, icon, color = "#1565C0", bg = "#E3F2FD", children, xs = 12, md = 6 }) => (
  <Grid item xs={xs} md={md}>
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "0.5px solid", borderColor: "divider", height: "100%" }}>
      <Box display="flex" alignItems="center" gap={1} mb={1.2}>
        <Box sx={{ color, display: "flex" }}>{icon}</Box>
        <Box sx={{ display: "inline-flex", px: 1.2, py: 0.3, borderRadius: 1.5, bgcolor: bg }}>
          <Typography variant="caption" fontWeight={700} sx={{ color, fontSize: 11, letterSpacing: 0.5 }}>
            {title}
          </Typography>
        </Box>
      </Box>
      {children}
    </Paper>
  </Grid>
);

/* ─── battery/charger sub-info block ─────────────────────────── */
const ItemInfoBlock = ({ item, index, type = "battery" }) => {
  if (!item) return null;

  const stock = item.stock || null;
  const sale  = item.sale  || null;

  const accentColor = type === "battery" ? "#1B5E20" : "#0D47A1";
  const accentBg    = type === "battery" ? "#E8F5E9" : "#E3F2FD";
  const label       = type === "battery"
    ? `Battery ${index + 1}`
    : "Charger";

  return (
    // ── mb={0} — spacing is now handled by the Grid container gap ──
    <Box mb={0}
      sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 1.5, overflow: "hidden", height: "100%" }}>

      {/* sub-header */}
      <Box sx={{ px: 1.5, py: 0.8, bgcolor: accentBg, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontSize={11} fontWeight={700} sx={{ color: accentColor }}>
          {label}
        </Typography>
        <Typography fontSize={11} fontWeight={700} sx={{ color: accentColor }}>
          {fmtMoney(item.batteryAmount ?? item.chargerAmount)}
        </Typography>
      </Box>

      <Box px={1.5} py={1}>
        {/* ── Stock info ── */}
        {stock ? (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ fontSize: 9, letterSpacing: 0.8, textTransform: "uppercase", display: "block", mb: 0.5 }}>
              Stock Info
            </Typography>
            <DR label="Sl. No."       value={stock.slNo} />
            <DR label="Company"       value={stock.companyName} />
            <DR label="Model"         value={stock.model} />
            <DR label="Serial No."    value={stock.serialNo} mono />
            {type === "battery" && (
              <DR label="Battery Type" value={stock.batteryType} />
            )}
            <DR label="Purchase Price" value={fmtMoney(stock.purchasePrice)} />
            <DR label="Sale Price"     value={fmtMoney(stock.salePrice)} />
            <DR label="Location"       value={stock.location} />
            <DR label="Status"         value={stock.status} />
          </>
        ) : (
          <Typography fontSize={11} color="text.secondary" py={0.5}>Stock info not available</Typography>
        )}

        <Divider sx={{ my: 1 }} />

        {/* ── Sale info ── */}
        {sale ? (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ fontSize: 9, letterSpacing: 0.8, textTransform: "uppercase", display: "block", mb: 0.5 }}>
              Sale Info
            </Typography>
            <DR label="Invoice No."    value={sale.invoiceNo} />
            <DR label="Invoice Serial" value={sale.invoiceSerial} />
            <DR label="Sale Item"      value={sale.saleItem} />
            <DR label="Sale Price"     value={fmtMoney(sale.salePrice)} />
            <DR label="Sale Date"      value={fmtDate(sale.saleDate)} />
            <DR label="Sale Type"      value={sale.saleType} />
            <DR label="Due Status"
              value={
                <Chip
                  label={sale.dueStatus || "—"}
                  size="small"
                  sx={{
                    height: 18, fontSize: 10, fontWeight: 700,
                    bgcolor: sale.dueStatus === "DUE"  ? "#FFF3E0" : "#E8F5E9",
                    color:   sale.dueStatus === "DUE"  ? "#E65100" : "#2E7D32",
                  }}
                />
              }
            />
            {sale.dueStatus === "DUE" && (
              <DR label="Remaining" value={fmtMoney(sale.remainingAmount)} />
            )}
            {sale.soldBy && (
              <DR label="Sold By" value={sale.soldBy?.name} />
            )}
          </>
        ) : (
          <Typography fontSize={11} color="text.secondary" py={0.5}>Sale info not available</Typography>
        )}
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
 *  BikeDetailModal — for EV sales (E-Rickshaw / Scooty)
 * ═══════════════════════════════════════════════════════════════ */
const BikeDetailModal = ({ open, onClose, selectedBike, detailLoading }) => {
  const d = selectedBike;
  console.log(d);

  const hasBatteries = Array.isArray(d?.battery) && d.battery.length > 0;
  const hasCharger   = d?.charger && (d.charger.stock || d.charger.sale);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}>
      <Box p={3} sx={{ overflowY: "auto", bgcolor: "#F8FAFC" }}>

        {/* ── header ── */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
          <Box>
            <Typography variant="h6" fontWeight={700}>EV Sale Full Details</Typography>
            {d && (
              <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                {d.invoiceNo && (
                  <Chip label={d.invoiceNo} size="small" color="primary" variant="outlined" />
                )}
                {d.vehicleType && (
                  <Chip label={d.vehicleType.replace("_", " ")} size="small" variant="outlined" />
                )}
                {d.model && (
                  <Chip label={d.model} size="small" variant="outlined" />
                )}
                {d.saleType && (
                  <Chip label={d.saleType} size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: d.saleType === "CASH"    ? "#e8f5e9"
                             : d.saleType === "FINANCE" ? "#e3f2fd"
                             : "#fff3e0",
                      color:   d.saleType === "CASH"    ? "#2e7d32"
                             : d.saleType === "FINANCE" ? "#1565c0"
                             : "#e65100",
                    }} />
                )}
              </Box>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {detailLoading && (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        )}

        {!detailLoading && !d && (
          <Alert severity="info">No data found</Alert>
        )}

        {!detailLoading && d && (
          <Grid container spacing={2}>

            {/* 1. VEHICLE INFO */}
            <VCard title="VEHICLE INFO" icon={<DirectionsBikeIcon fontSize="small" />} color="#1565C0" bg="#E3F2FD">
              <DR label="Vehicle Type"    value={d.vehicleType?.replace("_", " ")} />
              <DR label="Company / Brand" value={d.companyName} />
              <DR label="Model"           value={d.model} />
              <DR label="Engine No."      value={d.engineNumber} mono />
              <DR label="Serial No."      value={d.chasisNumber} mono />
              <DR label="Number Plate"    value={d.noPlate} />
              <DR label="Location"        value={d.location} />
              <DR label="Invoice No."     value={d.invoiceNo} />
              <DR label="Invoice Serial"  value={d.invoiceSerial} />
            </VCard>

            {/* 2. CUSTOMER */}
            <VCard title="CUSTOMER" icon={<PersonIcon fontSize="small" />} color="#2E7D32" bg="#E8F5E9">
              <DR label="Customer Name"  value={d.customerName} />
              <DR label="Father Name"    value={d.fatherName} />
              <DR label="Mobile"         value={Array.isArray(d.mobile) ? d.mobile.join(", ") : d.mobile} />
              <DR label="Address"        value={d.address} />
              <DR label="Village"        value={d.village} />
              <DR label="Post Office"    value={d.postOffice} />
              <DR label="Police Station" value={d.policeStation} />
              <DR label="PIN Code"       value={d.pinCode} />
              <DR label="Landmark"       value={d.landmark} />
              <DR label="GSTIN"          value={d.GSTIN} />
            </VCard>

            {/* 3. SALE DETAILS */}
            <VCard title="SALE DETAILS" icon={<CurrencyRupeeIcon fontSize="small" />} color="#E65100" bg="#FFF3E0">
              <DR label="Sale Price"      value={fmtMoney(d.salePrice)} />
              <DR label="ORP"             value={fmtMoney(d.orp)} />
              <DR label="Cash Amount"     value={fmtMoney(d.cashAmount)} />
              <DR label="Discount"        value={fmtMoney(d.discount)} />
              <DR label="Coupon Cashback" value={fmtMoney(d.couponCashback)} />
              <DR label="Sale Date"       value={fmtDate(d.saleDate)} />
              <DR label="Sale Type"       value={d.saleType} />
               <DR label="Help Battery"     value={d.helpBattery===true ? "Yes" : "No"} />
              <DR label="Help Battery Serial No" value={d.helpBatterySerial} />
              <DR label="Remarks"         value={d.remarks} />
            </VCard>

            {/* 4. FINANCE */}
            <VCard title="FINANCE" icon={<AccountBalanceIcon fontSize="small" />} color="#6A1B9A" bg="#F3E5F5">
              <DR label="Company Name" value={d.finance?.companyName} />
              <DR label="Amount"       value={fmtMoney(d.finance?.amount)} />
                            <DR label="financeDown Amount"       value={fmtMoney(d.finance?.financeDown)} />              
              <DR label="Months"       value={d.finance?.months} />
              <DR label="Date"         value={fmtDate(d.finance?.date)} />
            </VCard>

            {/* 5. DUE / INSTALLMENTS */}
            <VCard title="DUE / INSTALLMENTS" icon={<PendingActionsIcon fontSize="small" />}
              color={d.due?.status === "DUE" ? "#E65100" : "#2E7D32"}
              bg={d.due?.status    === "DUE" ? "#FFF3E0" : "#E8F5E9"}>
              <DR label="Status"       value={d.due?.status} />
              <DR label="Total Paid Amount" value={fmtMoney(d.cashAmount)} />
              <DR label="Total Due Amount" value={fmtMoney(d.due?.totalAmount)} />
              <DR label="Paid Amount"  value={fmtMoney(d.due?.paidAmount)} />
             <DR label="Remaining"    value={fmtMoney(d.due?.remainingAmount)} />
              <DR label="Due Remarks"  value={d.due?.DueRemarks} />

              {/* ── Download Slip button — always shown, before and after due is fully cleared ── */}
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <Button
                  size="small" variant="outlined"
                  startIcon={<ReceiptIcon fontSize="small" />}
                  onClick={() => openPaymentSlip(d)}
                  sx={{ borderRadius: 2, fontSize: 11, fontWeight: 700, textTransform: "none" }}
                >
                  Download slip
                </Button>
              </Box>
              {d.due?.payments?.length > 0 && (
                <Box mt={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    PAYMENT HISTORY ({d.due.payments.length})
                  </Typography>
                  {d.due.payments.map((p, i) => (
                    <Box key={i} display="flex" justifyContent="space-between" py={0.5}
                      sx={{ borderBottom: "0.5px solid", borderColor: "divider" }}>
                      <Typography fontSize={12} color="text.secondary">{fmtDate(p.date)}</Typography>
                      <Typography fontSize={12} color="text.secondary" sx={{ flex: 1, px: 1 }}>{p.note || "—"}</Typography>
                      <Typography fontSize={12} color="text.secondary" sx={{ flex: 1, px: 1 }}>{p.remark || "—"}</Typography>
                      <Typography fontSize={12} fontWeight={600}>{fmtMoney(p.amount)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </VCard>

            {/* 6. EXCHANGE */}
            {d.exchange?.registrationNumber && (
              <VCard title="EXCHANGE VEHICLE" icon={<SwapHorizIcon fontSize="small" />} color="#880E4F" bg="#FCE4EC">
                <DR label="Owner Name"     value={d.exchange.ownerName} />
                <DR label="Phone"          value={d.exchange.phone} />
                <DR label="Model"          value={d.exchange.model} />
                <DR label="Reg. No."       value={d.exchange.registrationNumber} mono />
                <DR label="Location"       value={d.exchange.location} />
                <DR label="Exchange Price" value={fmtMoney(d.exchange.exchangePrice)} />
                <DR label="Paid Amount"    value={fmtMoney(d.exchange.paidAmount)} />
                <DR label="Remaining"      value={fmtMoney(d.exchange.remainingAmount)} />
                <DR label="Status"         value={d.exchange.status} />
                {d.exchange.payments?.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      EXCHANGE PAYMENTS ({d.exchange.payments.length})
                    </Typography>
                    {d.exchange.payments.map((p, i) => (
                      <Box key={i} display="flex" justifyContent="space-between" py={0.5}
                        sx={{ borderBottom: "0.5px solid", borderColor: "divider" }}>
                        <Typography fontSize={12} color="text.secondary">{p.name || "—"}</Typography>
                        <Typography fontSize={12} color="text.secondary">{p.phone || "—"}</Typography>
                        <Typography fontSize={12} color="text.secondary">{fmtDate(p.date)}</Typography>
                        <Typography fontSize={12} fontWeight={600}>{fmtMoney(p.amount)}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </VCard>
            )}

            {/* 7. EXPENSES / FREEBIES */}
            <VCard title="EXPENSES / FREEBIES" icon={<CardGiftcardIcon fontSize="small" />} color="#00838F" bg="#E0F7FA">
              <DR label="Helmet (qty)"     value={d.expenses?.helmet} />
              <DR label="Seat Cover (qty)" value={d.expenses?.seatCover} />
              <DR label="Bike Cover (qty)" value={d.expenses?.bikeCover} />
              <DR label="Transport"        value={fmtMoney(d.expenses?.transport)} />
              <DR label="TCR"              value={fmtMoney(d.expenses?.tcr)} />
              <DR label="Reg. Broker"      value={fmtMoney(d.expenses?.regBroker)} />
            </VCard>

            {/* 8. CHARGES & GIFTS */}
            <VCard title="CHARGES & GIFTS" icon={<ReceiptIcon fontSize="small" />} color="#4527A0" bg="#EDE7F6">
              <DR label="RTO"       value={fmtMoney(d.charges?.rto)} />
              <DR label="Insurance" value={fmtMoney(d.charges?.insurance)} />
              {d.gifts?.length > 0 && (
                <Box mt={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                    GIFTS
                  </Typography>
                  {d.gifts.map((g, i) => (
                    <Box key={i} display="flex" justifyContent="space-between" py={0.4}
                      sx={{ borderBottom: "0.5px solid", borderColor: "divider" }}>
                      <Typography fontSize={12}>{g.name}</Typography>
                      <Typography fontSize={12} fontWeight={600}>×{g.quantity}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </VCard>

            {/* 9. TAX / GST */}
            {d.tax && (
              <VCard title="TAX / GST" icon={<ReceiptIcon fontSize="small" />} color="#546E7A" bg="#ECEFF1">
                <DR label="GST Rate"       value={`${d.tax.gstRate}%`} />
                <DR label="CGST"           value={`${d.tax.cgst}%`} />
                <DR label="SGST"           value={`${d.tax.sgst}%`} />
                <DR label="Taxable Amount" value={fmtMoney(d.tax.taxableAmount)} />
                <DR label="Total GST"      value={fmtMoney(d.tax.totalGstAmount)} />
              </VCard>
            )}

            {/* 10. SOLD BY */}
            <VCard title="SOLD BY" icon={<PersonIcon fontSize="small" />} color="#546E7A" bg="#ECEFF1">
              <DR label="Name"  value={d.soldBy?.name} />
              <DR label="Email" value={d.soldBy?.email} />
              <DR label="Role"  value={d.soldBy?.role} />
            </VCard>

            {/* 11. STOCK DETAILS */}
            {d.stockDetails && (
              <VCard title="STOCK DETAILS (ORIGINAL)" icon={<TwoWheelerIcon fontSize="small" />}
                color="#1565C0" bg="#E3F2FD" xs={12} md={12}>
                <Grid container spacing={1}>
                  {[
                    ["Location",   d.stockDetails.location],
                    ["Status",     d.stockDetails.status],
                    ["Source",     d.stockDetails.source],
                    ["Created By", d.stockDetails.createdBy?.name],
                  ].map(([label, value]) => (
                    <Grid item xs={6} md={3} key={label}>
                      <Box sx={{ p: 1, border: "0.5px solid", borderColor: "divider", borderRadius: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize={10}>
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} fontSize={12}>{value || "—"}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </VCard>
            )}

            {/* ═══════════════════════════════════════════════════════
             * 12. BATTERIES — 2-per-row grid layout
             * ═══════════════════════════════════════════════════════ */}
            {hasBatteries && (
              <VCard
                title={`BATTERIES (${d.battery.length})`}
                icon={<BatteryChargingFullIcon fontSize="small" />}
                color="#1B5E20" bg="#E8F5E9"
                xs={12} md={12}
              >
                <Grid container spacing={1.5}>
                  {d.battery.map((b, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <ItemInfoBlock item={b} index={i} type="battery" />
                    </Grid>
                  ))}
                </Grid>
              </VCard>
            )}

            {/* ═══════════════════════════════════════════════════════
             * 13. CHARGER — full width row below batteries
             * ═══════════════════════════════════════════════════════ */}
            {hasCharger && (
              <VCard
                title="CHARGER"
                icon={<EvStationIcon fontSize="small" />}
                color="#0D47A1" bg="#E3F2FD"
                xs={12} md={12}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <ItemInfoBlock item={d.charger} type="charger" />
                  </Grid>
                </Grid>
              </VCard>
            )}

          </Grid>
        )}

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}>
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BikeDetailModal;